<template>
  <div class="expand-text">
    <div
      ref="contentRef"
      class="expand-text__inner"
      :class="{
        'expand-text__inner--collapsed': needExpand && !isExpanded,
        'expand-text__inner--full': !needExpand || isExpanded,
      }"
      :style="{
        '--line-clamp': lineClamp,
        '--line-height': `${lineHeight}px`,
        '--collapsed-height': `${lineClamp * lineHeight}px`,
        '--float-helper-height': `${Math.max(0, lineClamp - 1) * lineHeight}px`,
        '--trigger-gap': `${triggerGap}px`,
      }"
    >
      <div v-if="needExpand && !isExpanded" class="expand-text__float-helper" />
      <div
        v-if="needExpand && !isExpanded"
        class="expand-text__trigger expand-text__trigger--float"
        @click="toggle"
      >
        <slot name="trigger" :expanded="isExpanded" :need-expand="needExpand">
          <span class="expand-text__trigger-default">
            {{ expandText }}
          </span>
        </slot>
      </div>
      <div class="expand-text__content">
        <slot />
      </div>
      <template v-if="needExpand && isExpanded">
        <span class="expand-text__trigger expand-text__trigger--inline" @click="toggle">
          <slot name="trigger" :expanded="isExpanded" :need-expand="needExpand">
            <span class="expand-text__trigger-default">{{ collapseText }}</span>
          </slot>
        </span>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, nextTick, watch } from 'vue'

const props = defineProps({
  /** 收起时显示的行数 */
  lineClamp: {
    type: Number,
    default: 2,
  },
  /** 展开按钮文案 */
  expandText: {
    type: String,
    default: '展开',
  },
  /** 收起按钮文案 */
  collapseText: {
    type: String,
    default: '收起',
  },
  /** 行高（px），用于计算是否超出，默认约 22（1.5 * 16） */
  lineHeight: {
    type: Number,
    default: 22,
  },
  /** 展开收起文字与省略号之间的间距（px） */
  triggerGap: {
    type: Number,
    default: 8,
  },
})

const emit = defineEmits(['change'])

const contentRef = ref(null)
const isExpanded = ref(false)
const needExpand = ref(false)
let resizeObserver = null

const toggle = () => {
  if (!needExpand.value) return
  isExpanded.value = !isExpanded.value
  emit('change', isExpanded.value)
}

const checkNeedExpand = () => {
  nextTick(() => {
    const el = contentRef.value
    if (!el) return

    // 临时移除限制以获取内容的真实完整高度
    const originalStyle = el.style.display
    const originalWebkitBox = el.style.webkitBoxOrient
    const originalClamp = el.style.webkitLineClamp

    el.style.display = 'block'
    el.style.webkitLineClamp = 'none'
    el.style.webkitBoxOrient = 'horizontal'

    const fullHeight = el.scrollHeight
    const limitHeight = props.lineClamp * props.lineHeight

    // 恢复原始样式
    el.style.display = originalStyle
    el.style.webkitLineClamp = originalClamp
    el.style.webkitBoxOrient = originalWebkitBox

    // 判断完整高度是否超过了限制行数的高度
    const isTruncated = fullHeight > limitHeight + 2
    needExpand.value = isTruncated
    if (!isTruncated) {
      isExpanded.value = false
    }
  })
}

onMounted(() => {
  nextTick(() => {
    setTimeout(checkNeedExpand, 50)
  })
  if (import.meta.client) {
    window.addEventListener('resize', checkNeedExpand)
    if (typeof ResizeObserver !== 'undefined' && contentRef.value) {
      resizeObserver = new ResizeObserver(checkNeedExpand)
      resizeObserver.observe(contentRef.value)
    }
  }
})

onUnmounted(() => {
  if (import.meta.client) {
    window.removeEventListener('resize', checkNeedExpand)
    resizeObserver?.disconnect()
    resizeObserver = null
  }
})

watch(
  () => [props.lineClamp, props.lineHeight],
  () => {
    needExpand.value = false
    isExpanded.value = false
    nextTick(() => {
      setTimeout(checkNeedExpand, 50)
    })
  }
)

defineExpose({
  isExpanded,
  needExpand,
  toggle,
  checkNeedExpand,
})
</script>

<script>
export default {
  name: 'ExpandText',
}
</script>

<style lang="scss" scoped>
.expand-text {
  display: block;
  width: 100%;
  box-sizing: border-box;
}

.expand-text__inner {
  position: relative;
  line-height: var(--line-height, 1.5);
  overflow: hidden;
  display: block;
  word-break: break-all;
  overflow-wrap: break-word;
  width: 100%;
  box-sizing: border-box;
}

.expand-text__inner--collapsed {
  max-height: var(--collapsed-height);
  overflow: hidden;
  display: -webkit-box;
  /* stylelint-disable-next-line value-no-vendor-prefix */
  display: -moz-box;
  -webkit-box-orient: vertical;
  -moz-box-orient: vertical;
  -webkit-line-clamp: var(--line-clamp, 2);
  line-clamp: var(--line-clamp, 2);
  text-align: justify;
  text-overflow: ellipsis;
}

.expand-text__inner--full {
  display: block;
}

.expand-text__content {
  display: inline;

  :deep(*) {
    display: inline;
    vertical-align: baseline;
  }
}

.expand-text__float-helper {
  float: right;
  width: 0;
  height: var(--float-helper-height);
}

.expand-text__trigger--float {
  float: right;
  clear: both;
  margin-left: var(--trigger-gap, 8px);
  position: relative;
  z-index: 1;
  line-height: var(--line-height);
  margin-top: 0;
  line-height: var(--line-height, 1.5);
  background: $bg-white;
}

.expand-text__trigger--inline {
  display: inline;
  margin-left: var(--trigger-gap, 8px);
  white-space: nowrap;
}

.expand-text__trigger {
  cursor: pointer;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  box-sizing: border-box;
}

.expand-text__trigger:active {
  opacity: 0.8;
}

.expand-text__trigger-default {
  font-size: $font-size-card-content;
  color: $primary-color;
}
</style>
